import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationsApi, calendarApi } from '../api';

const SEVERITY_ICON = { CRITICAL: '🔴', HIGH: '🟠', MEDIUM: '🟡', LOW: '⚪' };
const SEVERITY_LABEL = { CRITICAL: 'Kritisch', HIGH: 'Hoch', MEDIUM: 'Mittel', LOW: 'Niedrig' };
const SEVERITY_COLOR = { CRITICAL: '#ef4444', HIGH: '#f97316', MEDIUM: '#eab308', LOW: '#64748b' };

const card = { background: '#1e293b', borderRadius: 10, padding: '14px 16px', marginBottom: 10, border: '1px solid #334155' };
const chip = (active, color) => ({
  display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 12px',
  borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
  background: active ? (color || '#6366f1') : '#0f172a',
  color: active ? '#fff' : '#94a3b8', border: `1px solid ${active ? (color||'#6366f1') : '#334155'}`,
});
const btn = (variant = 'secondary') => ({
  padding: '5px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12,
  background: variant === 'primary' ? '#6366f1' : variant === 'danger' ? '#ef4444' : '#334155',
  color: '#fff', fontWeight: 500,
});

const FILTERS = [
  { key: 'all', label: 'Alle' },
  { key: 'unread', label: 'Ungelesen' },
  { key: 'critical', label: '🔴 Kritisch' },
  { key: 'action', label: 'Aufgaben' },
  { key: 'inbox', label: 'Posteingang' },
  { key: 'manual', label: 'Manuell' },
];

export default function BenachrichtigungenPage() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ title: '', message: '', scheduledFor: '' });
  const [creating, setCreating] = useState(false);
  const [snoozeId, setSnoozeId] = useState(null);
  const [snoozeDate, setSnoozeDate] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = {};
      if (filter === 'unread') params.unread = 'true';
      else if (filter === 'critical') params.severity = 'CRITICAL';
      else if (filter === 'action') params.sourceType = 'ACTION';
      else if (filter === 'inbox') params.sourceType = 'INBOX';
      else if (filter === 'manual') params.sourceType = 'MANUAL';
      const [list, sum] = await Promise.all([notificationsApi.list(params), notificationsApi.summary()]);
      setNotifications(list); setSummary(sum);
    } catch (e) { setError(e.message || 'Fehler beim Laden'); }
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  async function handleRead(id) {
    try { await notificationsApi.read(id); load(); } catch {}
  }
  async function handleDismiss(id) {
    try { await notificationsApi.dismiss(id); load(); } catch {}
  }
  async function handleReadAll() {
    try { await notificationsApi.readAll(); load(); } catch {}
  }
  async function handleSnooze(id) {
    if (!snoozeDate) return;
    try { await notificationsApi.snooze(id, snoozeDate); setSnoozeId(null); load(); } catch {}
  }
  async function handleCreate(e) {
    e.preventDefault(); if (!createForm.title || !createForm.scheduledFor) return;
    setCreating(true);
    try { await notificationsApi.createManual(createForm); setShowCreate(false); setCreateForm({ title:'', message:'', scheduledFor:'' }); load(); }
    catch (ex) { alert(ex.message); }
    setCreating(false);
  }

  const todayStr = new Date().toISOString().slice(0, 10);

  return (
    <div style={{ maxWidth: 700, margin: '0 auto', padding: '16px 12px', color: '#f1f5f9', fontFamily: 'system-ui,sans-serif' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <span style={{ fontSize: 24 }}>🔔</span>
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Benachrichtigungen</h1>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button style={btn('secondary')} onClick={handleReadAll} aria-label="Alle als gelesen markieren">Alle gelesen</button>
          <button style={btn('primary')} onClick={() => setShowCreate(!showCreate)} aria-label="Neue Erinnerung erstellen">+ Erinnerung</button>
        </div>
      </div>

      {summary && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
          <span style={chip(false)}>{summary.total} Offen</span>
          {summary.unread > 0 && <span style={chip(true, '#6366f1')}>{summary.unread} Ungelesen</span>}
          {summary.critical > 0 && <span style={chip(true, '#ef4444')}>{summary.critical} Kritisch</span>}
          {summary.today > 0 && <span style={chip(true, '#0891b2')}>{summary.today} Heute</span>}
          {summary.thisWeek > 0 && <span style={chip(false)}>{summary.thisWeek} diese Woche</span>}
        </div>
      )}

      {showCreate && (
        <form onSubmit={handleCreate} style={{ ...card, marginBottom: 16, borderColor: '#6366f1' }}>
          <div style={{ fontWeight: 700, marginBottom: 10, color: '#a5b4fc' }}>Neue Erinnerung erstellen</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <input required placeholder="Titel" value={createForm.title} onChange={e => setCreateForm(f => ({...f, title: e.target.value}))}
              style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 6, color: '#f1f5f9', padding: '8px 10px', fontSize: 14 }} />
            <input placeholder="Beschreibung (optional)" value={createForm.message} onChange={e => setCreateForm(f => ({...f, message: e.target.value}))}
              style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 6, color: '#f1f5f9', padding: '8px 10px', fontSize: 14 }} />
            <input required type="date" min={todayStr} value={createForm.scheduledFor} onChange={e => setCreateForm(f => ({...f, scheduledFor: e.target.value}))}
              style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 6, color: '#f1f5f9', padding: '8px 10px', fontSize: 14 }} />
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="submit" style={btn('primary')} disabled={creating}>Erstellen</button>
              <button type="button" style={btn('secondary')} onClick={() => setShowCreate(false)}>Abbrechen</button>
            </div>
          </div>
        </form>
      )}

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
        {FILTERS.map(f => (
          <button key={f.key} style={chip(filter === f.key)} onClick={() => setFilter(f.key)} aria-pressed={filter === f.key}>{f.label}</button>
        ))}
      </div>

      {error && (
        <div style={{ background: '#1e293b', border: '1px solid #ef4444', borderRadius: 8, padding: 14, marginBottom: 12, color: '#fca5a5' }}>
          {error} <button style={{ ...btn('secondary'), marginLeft: 8 }} onClick={load}>Retry</button>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', color: '#64748b', padding: 40 }}>Lädt…</div>
      ) : notifications.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#64748b', padding: 40 }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>🔔</div>
          <div>Keine Benachrichtigungen</div>
          {filter !== 'all' && <button style={{ ...btn('secondary'), marginTop: 10 }} onClick={() => setFilter('all')}>Alle anzeigen</button>}
        </div>
      ) : (
        notifications.map(n => (
          <div key={n.id} style={{ ...card, opacity: n.read_at ? 0.6 : 1, borderLeftColor: SEVERITY_COLOR[n.severity] || '#334155', borderLeftWidth: 3 }}>
            {snoozeId === n.id ? (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="date" min={todayStr} value={snoozeDate} onChange={e => setSnoozeDate(e.target.value)}
                  style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 6, color: '#f1f5f9', padding: '5px 8px', fontSize: 13 }} />
                <button style={btn('primary')} onClick={() => handleSnooze(n.id)} aria-label="Snooze bestätigen">OK</button>
                <button style={btn('secondary')} onClick={() => setSnoozeId(null)} aria-label="Abbrechen">✕</button>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
                  <span title={SEVERITY_LABEL[n.severity] || n.severity} style={{ fontSize: 16, flexShrink: 0 }}>{SEVERITY_ICON[n.severity] || '⚪'}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: n.read_at ? 400 : 700, fontSize: 14 }}>{n.title}</div>
                    {n.message && <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 2 }}>{n.message}</div>}
                    <div style={{ display: 'flex', gap: 10, marginTop: 4, fontSize: 11, color: '#64748b', flexWrap: 'wrap' }}>
                      <span>📅 {n.scheduled_for}</span>
                      {n.contract_title && <span>📋 {n.contract_title}</span>}
                      <span style={{ color: SEVERITY_COLOR[n.severity] || '#64748b' }}>{SEVERITY_LABEL[n.severity] || n.severity}</span>
                      {n.source_type === 'MANUAL' && <span style={{ color: '#a5b4fc' }}>Manuell</span>}
                      {n.read_at && <span>✓ Gelesen</span>}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {n.contract_id && (
                    <button style={btn('secondary')} onClick={() => navigate(`/vertraege/${n.contract_id}`)} aria-label="Vertrag öffnen">Vertrag</button>
                  )}
                  {!n.read_at && (
                    <button style={btn('secondary')} onClick={() => handleRead(n.id)} aria-label="Als gelesen markieren">Gelesen</button>
                  )}
                  <button style={btn('secondary')} onClick={() => { setSnoozeId(n.id); setSnoozeDate(''); }} aria-label="Erinnerung snoozen">Snooze</button>
                  <button style={{ ...btn('danger'), opacity: 0.8 }} onClick={() => handleDismiss(n.id)} aria-label="Erinnerung verwerfen">Verwerfen</button>
                </div>
              </>
            )}
          </div>
        ))
      )}

      <div style={{ ...card, marginTop: 24, borderColor: '#0891b2' }}>
        <div style={{ fontWeight: 700, marginBottom: 10, color: '#67e8f9' }}>📅 Kalender-Export</div>
        <div style={{ color: '#94a3b8', fontSize: 13, marginBottom: 10 }}>Alle Kündigungsfristen und Termine als ICS-Datei für Kalender-Apps</div>
        <a href="/api/kalender/export.ics" style={{ ...btn('primary'), textDecoration: 'none', display: 'inline-block' }} aria-label="Kalender als ICS exportieren">ICS exportieren</a>
      </div>
    </div>
  );
}
